import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.js";
import { ErrorBanner, LoadingSkeleton, Panel } from "../components/Common.js";

interface ProviderStatus {
  capabilities: { realtimeOptionsEntitled: boolean; historicalOptionsEntitled: boolean };
  rateLimit: { configuredLimitPerMinute: number; usedLastMinute: number; limitPerMinute: number };
  staleSymbolCount: number;
  backfillProgress: { ticker: string; tradingDaysBackfilled: number; tradingDaysExpected: number; isSampledOnly: boolean }[];
  recentJobs: { id: string; jobType: string; status: string; targetTicker: string | null }[];
}

export function AdminPage() {
  const queryClient = useQueryClient();
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [manualTicker, setManualTicker] = useState("");
  const [manualCompany, setManualCompany] = useState("");

  const statusQuery = useQuery({
    queryKey: ["admin-status"],
    queryFn: () => api.get<ProviderStatus>("/admin/market-data/status"),
  });

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get<{ users: { email: string; role: string }[]; admins: { email: string }[] }>("/admin/users"),
  });

  const addAdminMutation = useMutation({
    mutationFn: (email: string) => api.post("/admin/admins", { email }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const addSecurityMutation = useMutation({
    mutationFn: () => api.post("/admin/universe/add", { ticker: manualTicker, companyName: manualCompany }),
  });

  if (statusQuery.error) return <ErrorBanner message={(statusQuery.error as Error).message + " (admin access required)"} />;

  return (
    <div className="space-y-3">
      <Panel title="Alpha Vantage provider status">
        {statusQuery.isLoading && <LoadingSkeleton rows={4} />}
        {statusQuery.data && (
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              Realtime options: {statusQuery.data.capabilities.realtimeOptionsEntitled ? "entitled" : "not entitled (using historical chain)"}
            </div>
            <div>
              Rate limit: {statusQuery.data.rateLimit.usedLastMinute}/{statusQuery.data.rateLimit.limitPerMinute} req/min
            </div>
            <div>Stale symbols: {statusQuery.data.staleSymbolCount}</div>
            <div>Configured cap: {statusQuery.data.rateLimit.configuredLimitPerMinute}/min</div>
          </div>
        )}
      </Panel>

      <Panel title="Backfill completeness">
        {statusQuery.data && (
          <table className="w-full text-left text-xs">
            <thead className="uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1">Ticker</th>
                <th className="px-2 py-1">Days backfilled</th>
                <th className="px-2 py-1">Sampled only</th>
              </tr>
            </thead>
            <tbody>
              {statusQuery.data.backfillProgress.slice(0, 20).map((b) => (
                <tr key={b.ticker} className="border-t border-surface-border/60 font-mono">
                  <td className="px-2 py-1">{b.ticker}</td>
                  <td className="px-2 py-1">
                    {b.tradingDaysBackfilled}/{b.tradingDaysExpected}
                  </td>
                  <td className="px-2 py-1">{b.isSampledOnly ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Users & admins">
        {usersQuery.data && (
          <>
            <div className="mb-2 flex gap-2">
              <input
                className="field-input max-w-xs"
                placeholder="email@company.com"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
              />
              <button className="btn" onClick={() => addAdminMutation.mutate(newAdminEmail)}>
                Grant admin
              </button>
            </div>
            <div className="text-xs text-slate-400">Current admins: {usersQuery.data.admins.map((a) => a.email).join(", ") || "none"}</div>
          </>
        )}
      </Panel>

      <Panel title="Universe: add manual ticker">
        <div className="flex flex-wrap gap-2">
          <input className="field-input max-w-[8rem]" placeholder="Ticker" value={manualTicker} onChange={(e) => setManualTicker(e.target.value.toUpperCase())} />
          <input className="field-input max-w-xs" placeholder="Company name" value={manualCompany} onChange={(e) => setManualCompany(e.target.value)} />
          <button className="btn" onClick={() => addSecurityMutation.mutate()}>
            Add
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Manually-added tickers are flagged and never removed by an automated universe refresh.
        </p>
      </Panel>

      <Panel title="Recent refresh jobs">
        {statusQuery.data && (
          <table className="w-full text-left text-xs">
            <thead className="uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1">Job</th>
                <th className="px-2 py-1">Ticker</th>
                <th className="px-2 py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {statusQuery.data.recentJobs.slice(0, 15).map((j) => (
                <tr key={j.id} className="border-t border-surface-border/60 font-mono">
                  <td className="px-2 py-1">{j.jobType}</td>
                  <td className="px-2 py-1">{j.targetTicker ?? "—"}</td>
                  <td className={`px-2 py-1 ${j.status === "failed" ? "text-red-400" : "text-slate-300"}`}>{j.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
